import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DatasetActionContainerService } from 'app/main/components/dataset/services/dataset-action-container.service';
import { FormViewerComponent } from 'app/main/components/form-viewer/form-viewer.component';
import { TrenitaliaService } from '../../trenitalia.service';
import { FormViewerService } from '../../../../../../services/form-viewer.service';
import { fieldsMap as formFields } from './form.fields';
import { BehaviorSubject, Subject } from 'rxjs';
import * as _ from 'lodash';
import moment from 'moment';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {Validators} from "@angular/forms";
import { MatSnackBar } from '@angular/material/snack-bar';

const fieldKeyMap = {
	pass_first_name: 'name',
	pass_last_name: 'surname',
	pass_email: 'email',
	pass_data_n: 'birthDate',
	pass_phone: 'phoneNumber',
	pass_cf: 'cartaFreccia',
	contact_consent: 'contact_consent'
};
const travellerKeyMap = _.invert(fieldKeyMap); 

export function travellerKeyToFieldKey(travellerKey: string): string{
	return _.get(travellerKeyMap, travellerKey, travellerKey);
}

export function fieldKeyToTravellerKey(fieldKey: string): string{
	return _.get(fieldKeyMap, fieldKey, fieldKey);
}

@Component({
    selector: 'trenitalia-passenger-form',
    templateUrl: './passenger-form.component.html',
    styleUrls: ['./passenger-form.component.scss'],
    providers: [FormViewerService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PassengerFormComponent implements OnInit, OnChanges, OnDestroy {
	@Input('index') index: number;
	@Input('contactData') contactData: BehaviorSubject<any>;
	@Input('passenger') passenger: any;
	@Input('copyContactData') copyContactData: boolean = false;
	@Input('isRoundTrip') isRoundTrip: boolean = false;
	@Input('canApplyDiscount') canApplyDiscount: boolean = true;
	public showDiscountCode = false;
	/** key of the field being checked, null when idle */
	public checkingDiscountCode: string = null;
	public formViewer: FormViewerComponent;
	public fields = formFields;
	@Output('validChild') valid = new EventEmitter();
	public isReadOnly = false;
	private invalidTravellerParameters;

	protected _unsubscribeAll: Subject<void>;

	constructor(
		public datasetACS: DatasetActionContainerService,
		private _trnService: TrenitaliaService,
		public formViewerService: FormViewerService,
		public tranitaliaservice: TrenitaliaService,
		private snackBar: MatSnackBar
	) {
		this._unsubscribeAll = new Subject<any>();
	}

	/**
	 * Looks a discount code up before confirming. Applying it consumes it on
	 * Trenitalia side, so the operator gets to know upfront whether it is
	 * expired or already used instead of finding out when the booking fails.
	 */
	verifyDiscountCode(fieldKey: string) {
		const discountCode = _.trim(_.get(this.formViewerService.getFormData(), fieldKey, ''));
		if (!discountCode) {
			this.showDiscountMessage(false, 'Inserire un codice sconto da verificare.');
			return;
		}
		this.checkingDiscountCode = fieldKey;
		this._trnService.searchDiscountCode({discountCode})
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe({
			next: response => {
				this.checkingDiscountCode = null;
				const details = this.findDiscountCodeDetails(response);
				if (!details) {
					this.showDiscountMessage(false, 'Codice sconto non trovato.');
					return;
				}
				const message = this.describeDiscountCode(details);
				if (this.isDiscountCodeSpent(details)) this.showDiscountMessage(false, message);
				else this.showDiscountMessage(true, message);
			},
			error: error => {
				this.checkingDiscountCode = null;
				this.showDiscountMessage(false, _.get(error, 'error.message') || 'Codice sconto non trovato.');
			}
		});
	}

	/**
	 * Own snackbar instead of AppService: the colour comes from classes scoped to
	 * the discount code, leaving every other message in the app as it was.
	 */
	private showDiscountMessage(ok: boolean, message: string) {
		const snackBarRef = this.snackBar.open(message, 'DISMISS', {
			duration: ok ? 3500 : 4500,
			panelClass: [ok ? 'discount-code-snackbar-ok' : 'discount-code-snackbar-ko']
		});
		snackBarRef.onAction().subscribe(() => snackBarRef.dismiss());
	}

	/** the response nests the code at a depth that depends on the SOAP wrapper */
	private findDiscountCodeDetails(node: any) {
		if (!_.isObject(node)) return null;
		if (_.has(node, 'endValidity') || _.has(node, 'discountAmount') || _.has(node, 'discountRate')) return node;
		for (const value of Object.values(node)) {
			const found = this.findDiscountCodeDetails(value);
			if (found) return found;
		}
		return null;
	}

	private isDiscountCodeSpent(details: any): boolean {
		if (String(_.get(details, 'voided')) === 'true') return true;
		if (String(_.get(details, 'activated')) === 'false') return true;
		if (this.isDiscountCodeOutOfValidity(details)) return true;
		if (String(_.get(details, 'used')) === 'true') return true;
		if (String(_.get(details, 'unlimitedUsage')) === 'true') return false;
		const maxUsages = Number(_.get(details, 'maxUsages', 0));
		const currentUsages = Number(_.get(details, 'currentUsages', 0));
		return Boolean(maxUsages && currentUsages >= maxUsages);
	}

	private isDiscountCodeOutOfValidity(details: any): boolean {
		const endValidity = _.get(details, 'endValidity');
		if (endValidity && moment(endValidity).isValid() && moment(endValidity).isBefore(moment())) return true;
		const startValidity = _.get(details, 'startValidity');
		return Boolean(startValidity && moment(startValidity).isValid() && moment(startValidity).isAfter(moment()));
	}

	private describeDiscountCode(details: any): string {
		const parts = [];
		const rate = Number(_.get(details, 'discountRate', 0));
		const amount = Number(_.get(details, 'discountAmount.amount', 0));
		if (rate) parts.push('sconto del ' + rate + '%');
		else if (amount) parts.push('sconto di ' + amount + ' €');

		const endValidity = _.get(details, 'endValidity');
		if (endValidity && moment(endValidity).isValid()) {
			const expired = moment(endValidity).isBefore(moment());
			parts.push((expired ? 'SCADUTO il ' : 'valido fino al ') + moment(endValidity).format('DD/MM/YYYY'));
		}
		const startValidity = _.get(details, 'startValidity');
		if (startValidity && moment(startValidity).isValid() && moment(startValidity).isAfter(moment())) {
			parts.push('utilizzabile dal ' + moment(startValidity).format('DD/MM/YYYY'));
		}

		if (String(_.get(details, 'unlimitedUsage')) === 'true') parts.push('utilizzi illimitati');
		else if (_.has(details, 'maxUsages')) parts.push('utilizzi ' + Number(_.get(details, 'currentUsages', 0)) + '/' + Number(_.get(details, 'maxUsages', 0)));

		const prefix = this.isDiscountCodeSpent(details) ? 'Codice sconto non utilizzabile' : 'Codice sconto valido';
		return parts.length ? prefix + ': ' + parts.join(', ') + '.' : prefix + '.';
	}

	toggleDiscountCode() {
		this.showDiscountCode = !this.showDiscountCode;
	}

	ngOnInit() {
		this.formViewerService.init({
			errors: null,
			dataContainerService: this.datasetACS,
			fields: Array.from(formFields.values())
		})
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(initialized => {
			if(!initialized) return;
            console.log(this.passenger);
			if(!_.isEmpty(this.passenger)){
				this.formViewerService.setValues({
					pass_first_name: _.get(this.passenger, 'dataPass.pass_first_name', ''),
					pass_last_name: _.get(this.passenger, 'dataPass.pass_last_name', ''),
					pass_email: _.get(this.passenger, 'dataPass.pass_email', ''),
					pass_data_n: _.get(this.passenger, 'dataPass.pass_data_n', null),
					pass_phone: _.get(this.passenger, 'dataPass.pass_phone', null),
					pass_cf: _.get(this.passenger, 'dataPass.pass_cf', null),
					pass_discount_code_fw: _.get(this.passenger, 'dataPass.pass_discount_code_fw', null),
					pass_discount_code_rt: _.get(this.passenger, 'dataPass.pass_discount_code_rt', null),
					contact_consent: _.get(this.passenger, 'dataPass.contact_consent', true)
				});
				// keep the panel open when coming back to this step with a code already typed in
				this.showDiscountCode = Boolean(_.get(this.passenger, 'dataPass.pass_discount_code_fw') || _.get(this.passenger, 'dataPass.pass_discount_code_rt'));
			}
			this.setContactData();
		});

		this.tranitaliaservice.checkoutData
		.pipe(takeUntil(this._unsubscribeAll), distinctUntilChanged(_.isEqual))
		.subscribe(checkoutData => {
			if(!_.get(this.passenger, 'travellerXmlId')){
				console.warn('no travellerXmlId setted', this.passenger);
				return;
			}
			const errors = _.get(checkoutData, 'invalid_traveller_parameters.'+this.passenger.travellerXmlId);
			const changed = !_.isEqual(this.invalidTravellerParameters, errors);
			this.invalidTravellerParameters = errors;
			if(!errors){
				if(changed) {
					this.formViewerService.setErrors(null);
					this.formViewerService.formGroup.updateValueAndValidity(/*{emitEvent: false}*/);
				}
				return;
			}
			const errorMessages = Object.entries(errors).reduce((result, [key, paramData]) => {
				if(!_.isEmpty(_.get(paramData, 'validationMessages'))) result[travellerKeyToFieldKey(key)] = _.get(paramData, 'validationMessages');
				return result;
			}, {});
			this.formViewerService.setErrors(errorMessages);
		});

		this.formViewerService.valueChanges
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(v => {
            if(this.passenger.type==='CHILDREN') {
                if(!this.formViewerService.formGroup.get('pass_data_n').value){
                    const errorData={'pass_data_n':['Data obbligatoria']};
                    this.formViewerService.setErrors(errorData);
                }else{
                    /*Controllo età*/
                    /*const date = this.formViewerService.formGroup.get('pass_data_n').value;
                    const diff = moment().diff(date,'years');
                    if(diff > 15 || diff < 6){
                        const errorData={'pass_data_n':['Data non valida']};
                        this.formViewerService.setErrors(errorData);
                    }*/
                }
            }
			this.sendData();
		});

		if(this.index == 0 && this.contactData){
			this.contactData
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(s => {
				if(!this.copyContactData) return;
				if (!_.isEmpty(s)) {
					this.setContactData();
				} else {
					this.formViewerService.formGroup.reset();
				}
			});
		}
	}

	disableInput(key: string, disable = true): void{
		const control = this.formViewerService.getFieldControl(key);
		if(!control) return;
		if(disable) control.disable();
		else control.enable();
	}

	setContactData(){
		this.isReadOnly = this.index == 0 && this.copyContactData;
		if(this.index != 0 || !this.copyContactData) return;
		this.formViewerService.setValues({
			pass_first_name: this.contactData.value.first_name,
			pass_last_name: this.contactData.value.last_name,
			pass_email: this.contactData.value.email,
			pass_phone: this.contactData.value.phone
		});
	}

	ngOnChanges(changes: SimpleChanges): void{
		if(changes.copyContactData){
			this.setContactData();
		}
	}

	sendData() {
		console.log('sendData', this.index, this.formViewerService.formGroup.valid, this.formViewerService.getFormErrors());
		this.valid.emit({
			index: this.index,
			valid: this.formViewerService.formGroup.valid,
			data: this.formViewerService.getFormData()
		});
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next();
		this._unsubscribeAll.complete();
	}
}
